import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Loader2, RefreshCw, BarChart3, Send, ExternalLink, Users, Eye, Mail, Calendar } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const FRIENDLY_PATH = {
  "/": "Landing (/)",
  "/speaking": "Speaking (/speaking)",
  "/trial": "Trial (/trial)",
  "/start-trial": "Trial (/start-trial)",
  "/signin": "Sign in (/signin)",
  "/demo": "Try-a-scenario (/demo)",
  "/demo/sample-report": "Sample report (/demo/sample-report)",
};
const FRIENDLY_EVENT = {
  nav_click: "Universal nav clicks",
  signin_click: "Sign in button",
  trial_click: "Start 21-Day Trial button",
  talk_to_team_click: "Talk to our team",
  try_scenario_click: "Try a scenario",
  book_speaker_click: "Book Dr. Perkins",
  reel_play: "Speaker reel plays",
};
const FRIENDLY_NAV = {
  how: "How it works", hmde: "HMDE engine", "modules-section": "Four modules",
  outcomes: "Outcomes", clinical: "Clinical foundation", speaking: "Speaking",
};

const Stat = ({ label, value, tone = "leaf", testid }) => (
  <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-bold" style={{ color: `hsl(var(--haruki-${tone}))` }}>
      {value?.toLocaleString?.() ?? value ?? "—"}
    </div>
  </div>
);

const Table = ({ head, rows, empty, testid }) => (
  <div className="overflow-hidden rounded-lg border border-border bg-card" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="bg-muted/40">
        <tr>{head.map(h => <th key={h.k} className={`px-3 py-2 text-${h.align || "left"} text-[11px] font-semibold uppercase tracking-widest text-muted-foreground`}>{h.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={head.length} className="px-3 py-6 text-center text-xs italic text-muted-foreground">{empty}</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i} className="border-t border-border">
            {head.map(h => <td key={h.k} className={`px-3 py-2 text-${h.align || "left"}`}>{h.render ? h.render(r) : r[h.k]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PlatformAnalytics() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try { const r = await api.get(`/platform/analytics/report?days=${d}`); setReport(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load report."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(days); }, [load, days]);

  const sendNow = async () => {
    if (!window.confirm("Send the bi-weekly report email now?")) return;
    setSending(true);
    try {
      const r = await api.post("/platform/analytics/send-report");
      if (r.data?.ok) toast.success(`Report sent to ${r.data.recipient}.`);
      else toast.error(r.data?.error || "Send failed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <BarChart3 className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>Admin · Site analytics</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/platform" className="text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="mr-1 inline h-3 w-3" />Platform overview
            </Link>
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold">Site analytics</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Cookie-less, self-hosted tracking. Bi-weekly report auto-mails <strong>kariesha.drayton@gmail.com</strong> every other Monday 8am ET.
            </p>
          </div>
          <div className="flex gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                    data-testid="analytics-window-select"
                    className="rounded-md border border-border bg-background px-2 py-1.5 text-xs">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button variant="outline" size="sm" onClick={() => load(days)}>
              <RefreshCw className="mr-1.5 h-3 w-3" /> Refresh
            </Button>
            <Button size="sm" onClick={sendNow} disabled={sending} data-testid="analytics-send-now">
              {sending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Send className="mr-1.5 h-3 w-3" />}
              Send report now
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !report ? (
          <div className="mt-10 rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No data.
          </div>
        ) : (
          <>
            {report.totals.pageviews === 0 && (
              <div className="mt-6 rounded-lg border-l-4 border-amber-500 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200"
                   data-testid="analytics-activation-banner">
                <strong>Tracking just activated.</strong> Traffic and click data starts recording from this point forward — the next report will contain full numbers.
              </div>
            )}

            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Pageviews" value={report.totals.pageviews} tone="leaf" testid="stat-pageviews" />
              <Stat label="Unique visitors" value={report.totals.unique_visitors} tone="teal" testid="stat-visitors" />
              <Stat label="Trial signups" value={report.conversions.trial_intakes} tone="leaf" testid="stat-signups" />
              <Stat label="Speaking bookings" value={report.conversions.speaking_bookings} tone="teal" testid="stat-bookings" />
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <div>
                <h2 className="mb-3 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  <Eye className="mr-1.5 inline h-3.5 w-3.5" /> Where people are landing
                </h2>
                <Table
                  head={[
                    { k: "path", label: "First page (landing)", render: r => FRIENDLY_PATH[r.path] || r.path },
                    { k: "sessions", label: "Sessions", align: "right" },
                  ]}
                  rows={report.landing_pages}
                  empty="No landing data yet in this window."
                  testid="landing-table"
                />
              </div>

              <div>
                <h2 className="mb-3 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  <BarChart3 className="mr-1.5 inline h-3.5 w-3.5" /> Top pages viewed
                </h2>
                <Table
                  head={[
                    { k: "path", label: "Page", render: r => FRIENDLY_PATH[r.path] || r.path },
                    { k: "views", label: "Views", align: "right" },
                    { k: "sessions", label: "Sessions", align: "right" },
                  ]}
                  rows={report.top_pages}
                  empty="No pageviews yet in this window."
                  testid="top-pages-table"
                />
              </div>
            </div>

            <div className="mt-8">
              <h2 className="mb-3 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">
                Button clicks
              </h2>
              <Table
                head={[
                  { k: "name", label: "Action", render: r => FRIENDLY_EVENT[r.name] || r.name },
                  { k: "count", label: "Clicks", align: "right" },
                  { k: "unique_sessions", label: "Unique sessions", align: "right" },
                ]}
                rows={report.events}
                empty="No button clicks tracked yet."
                testid="events-table"
              />
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <div>
                <h2 className="mb-3 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Universal nav — breakdown
                </h2>
                <Table
                  head={[
                    { k: "target", label: "Nav item", render: r => FRIENDLY_NAV[r.target] || r.target || "(unknown)" },
                    { k: "count", label: "Clicks", align: "right" },
                  ]}
                  rows={report.nav_breakdown}
                  empty="No nav clicks yet."
                  testid="nav-table"
                />
              </div>
              <div>
                <h2 className="mb-3 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  <ExternalLink className="mr-1.5 inline h-3.5 w-3.5" /> Traffic sources
                </h2>
                <Table
                  head={[
                    { k: "referrer", label: "Referrer", render: r => <span className="break-all">{r.referrer || "Direct / typed in"}</span> },
                    { k: "sessions", label: "Sessions", align: "right" },
                  ]}
                  rows={report.referrers}
                  empty="All traffic looks direct so far."
                  testid="referrers-table"
                />
              </div>
            </div>

            <div className="mt-8 rounded-lg border border-border bg-card p-4 text-sm">
              <strong>Funnel:</strong> <span className="font-mono">{report.conversions.trial_click_to_signup_pct}%</span>
              &nbsp;of trial-button clicks completed the trial signup form in this window.
            </div>

            <div className="mt-6 text-xs text-muted-foreground">
              Window: {new Date(report.window.since).toLocaleDateString()} → {new Date(report.window.until).toLocaleDateString()}
              &nbsp;·&nbsp; Generated: {new Date(report.generated_at).toLocaleString()}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
